/* eslint-disable no-confusing-arrow */
import React, { useState, forwardRef, useLayoutEffect, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import Graphemer from 'graphemer';
import classNames from 'classnames';
import { regular, book, colors } from '@/styles';
import { RadioCircleSelect } from '@/svgs';
import { inputDictionary } from '@/constants';
import { useEnsuredForwardedRef } from '@/functions';

/**
 * InputField component
 *
 * @version 1.0.0
 * @param {import('.').InputFieldProps} props
 * @param {React.ForwardedRef} ref
 */
function InputField(
  {
    className,
    type,
    placeholder,
    name,
    onBlur,
    onChange,
    onFocus,
    onKeyDown,
    maxLength,
    labelName,
    showCharacterCount,
    showLabel,
    showSuccess,
    required,
    value,
    autoComplete,
    customValidator,
    onValidationChange,
    isRebrand,
    outline,
    disabled,
    icon,
    iconSize,
  },
  ref,
) {
  const [textLength, setTextLength] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [focused, setFocus] = useState(false);
  const [showError, setShowError] = useState(null);
  const ensuredRef = useEnsuredForwardedRef(ref);

  const handleFocus = useCallback(
    (event) => {
      if (onFocus) {
        onFocus(name, event);
      }
      setFocus(true);
    },
    [onFocus, name],
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (onKeyDown) {
        onKeyDown(name, event);
      }
    },
    [onKeyDown, name],
  );

  const showErrorMessage = useCallback(
    (validityError, errorText) => {
      ensuredRef.current?.setCustomValidity(validityError);
      setShowError(true);
      setErrorMessage(errorText);
      setFocus(true);
    },
    [ensuredRef],
  );

  const handleValidation = useCallback(
    (targetValue) => {
      const inputRegexTypes = ['email', 'tel', 'url', 'multiLink'];
      // Handles the validation for required fields
      if (targetValue.length === 0 && required) {
        setTextLength(0);
        showErrorMessage('is required', 'is required');
        return;
      }
      if (maxLength && Number(maxLength) > 0) {
        /* NOTE: This is here to better support emoji in text fields.
         * When using `string.length` the browser returns the number of code units in the string
         * not characters. One proposed solution around this was to instead use `[...string].length`
         * which will return the number of characters, however many emoji are comprised of multiple
         * characters. What we really need to count is the user-perceived characters also known
         * as Grapheme Clusters. There is a built-in method for getting this count in most browsers
         * ```
         * const segmenter = new Intl.Segmenter('en');
         * const string1 = 'a line of text';
         * const iterator1 = [...segmenter.segment(string1)];
         * console.log(iterator1.length);
         * ```
         * This however is not currently supported in Firefox. Due to needing to support Firefox we
         * are using a third-party library to get this count */
        const splitter = new Graphemer();
        const newTextLength = splitter.countGraphemes(targetValue);
        setTextLength(newTextLength);

        if (newTextLength > Number(maxLength)) {
          showErrorMessage('is too long', `of ${maxLength} characters`);
          return;
        }
      }
      if (inputRegexTypes.includes(type) && !inputDictionary[type].test(targetValue)) {
        showErrorMessage('is invalid', 'is invalid');
        return;
      }
      if (customValidator) {
        const [valid, reason] = customValidator(targetValue);
        if (!valid) {
          showErrorMessage('is invalid', reason);
          return;
        }
      }
      /** ensuredRef should not be null or undefined yet when running
       * Virtual Background Library › should test updating background name it is. Why? */
      ensuredRef?.current?.setCustomValidity('');
      setShowError(false);
      setFocus(true);
    },
    [maxLength, ensuredRef, customValidator, required, type, showErrorMessage],
  );

  const handleBlur = useCallback(
    (event) => {
      const { value: targetValue } = event.target;

      handleValidation(targetValue);
      if (onBlur) {
        onBlur(name, event);
      }
    },
    [onBlur, handleValidation, name],
  );

  const handleChange = useCallback(
    (event) => {
      const { value: targetValue } = event.target;

      handleValidation(targetValue);
      onChange(name, targetValue);
    },
    [onChange, name, handleValidation],
  );

  /* label should be rendered in position, not triggered after first paint */
  useLayoutEffect(() => {
    if (value.length !== 0) {
      setFocus(true);
    }
    /* We only want to run this on first load */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (value) {
      handleValidation(value);
    }
  }, [value, handleValidation]);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(!showError);
    }
  }, [showError, onValidationChange]);

  return (
    <div className={classNames(className, { focused, outline, rebrand: isRebrand })}>
      {icon && <div className="icon">{icon}</div>}
      <input
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        className={classNames({ error: showError })}
        // every type except multiLink are valid HTML input types
        type={type !== 'multiLink' ? type : 'text'}
        name={name}
        value={value}
        required={required}
        id={name}
        placeholder={labelName ? '' : placeholder}
        ref={ensuredRef}
        autoComplete={autoComplete}
        disabled={disabled}
      />
      {showLabel && <label htmlFor={name}>{labelName}</label>}
      {showSuccess && showError === false && (
        <div className="success">
          <RadioCircleSelect
            size={isRebrand ? '4.91' : '6'}
            fill={isRebrand ? colors.electricBlue : colors.kermit}
          />
        </div>
      )}
      <div className="status-line">
        <div className="error-style">{showError && `${placeholder} ${errorMessage}`}</div>
        <div className="counter">{showCharacterCount && maxLength - textLength}</div>
      </div>
    </div>
  );
}

/* eslint-disable-next-line no-func-assign */
InputField = forwardRef(InputField);

InputField.propTypes = {
  /** Emotion base style */
  className: PropTypes.string.isRequired,
  /** The type of input field */
  type: PropTypes.oneOf(['text', 'email', 'tel', 'password', 'url', 'number', 'multiLink']),
  /** The placeholder value for the input */
  placeholder: PropTypes.string.isRequired,
  /** The name for the input */
  name: PropTypes.string.isRequired,
  /** Called when the input control loses focus. */
  onBlur: PropTypes.func,
  /** Function to handle the onChange on the Form Level */
  onChange: PropTypes.func.isRequired,
  /** Called when the input control gains focus. */
  onFocus: PropTypes.func,
  /** Called when keys are entered in the input control. Use event.preventDefault() to stop
   * processing. */
  onKeyDown: PropTypes.func,
  /** The max length for the input */
  maxLength: PropTypes.string,
  /** The label name for the input */
  labelName: PropTypes.string,
  /** Flag indicating whether to show the remaining number of characters.
   * Requires maxLength to be set */
  showCharacterCount: PropTypes.bool,
  /** Logic to determine if the label is visibile or not */
  showLabel: PropTypes.bool,
  /** Flag indicating whether to show the success mark when validation passes. */
  showSuccess: PropTypes.bool,
  /** Whether or not the input is a required field */
  required: PropTypes.bool,
  /** The value for the input */
  value: PropTypes.string,
  /** Optionally enable/disable input autocomplete  */
  autoComplete: PropTypes.oneOf(['on', 'off']),
  /** Optional custom validation function. Ran in addition to other validations.
   * Takes in a value string should return [valid: boolean, invalid reason: string] */
  customValidator: PropTypes.func,
  /** Callback function to get the fields valid status and when the status changes.
   * Called with a single boolean parameter indicating the field is valid ( true )
   * or invalid ( false ) */
  onValidationChange: PropTypes.func,
  /** Should the input field have the rebrand font, colors, and style */
  isRebrand: PropTypes.bool,
  /** Surrounds the input field with an outline instead of the usual underline.
   *  Uses some rebrand colors. */
  outline: PropTypes.bool,
  /** Whether or not the input is disabled */
  disabled: PropTypes.bool,
  /** An icon element to use, should be size 4.91 for rebrand */
  icon: PropTypes.node,
  /** Icon sizing to add padding as needed for larger icons */
  iconSize: PropTypes.string,
};

InputField.defaultProps = {
  labelName: '',
  maxLength: '',
  onBlur: null,
  onFocus: null,
  onKeyDown: null,
  required: false,
  showCharacterCount: false,
  showLabel: true,
  showSuccess: true,
  type: 'text',
  value: '',
  autoComplete: null,
  customValidator: null,
  onValidationChange: null,
  isRebrand: false,
  outline: false,
  disabled: false,
  icon: null,
  iconSize: null,
};

const styledInputField = styled(InputField)`
  ${book()}
  position: relative;

  --input-padding: ${(props) => (props.outline ? '0.75rem' : '0.25rem')};
  /* space that the label fills, and minimum space underneath */
  --input-margin: ${(props) => (props.outline ? '1.25rem' : '1rem')};
  --icon-padding: ${(props) =>
    props.icon ? `calc(${props.iconSize / 2}rem + var(--input-padding))` : 'var(--input-padding)'};
  --value-font-size: 0.9rem;

  input {
    ${book()}
    margin: var(--input-margin) 0;
    padding: var(--input-padding);
    padding-right: ${(props) => (props.showSuccess ? '1.75rem' : 'var(--input-padding)')};
    padding-left: var(--icon-padding);
    width: 100%;
    border: none;
    border-bottom: 0.0625rem solid ${colors.silverSand};
    box-sizing: border-box;
    background: transparent;
    color: ${colors.boulder};
    font-size: var(--value-font-size);

    &::placeholder {
      color: ${colors.boulder};
      font-size: var(--value-font-size);
    }

    &:focus {
      outline: none;
      border-bottom-color: ${colors.battleship};
    }

    &.error {
      border-color: ${colors.recordRed};
    }

    &:disabled {
      border-color: ${colors.silverSand};
    }
  }

  label {
    color: ${(props) => (props.disabled ? colors.silverSand : colors.boulder)};

    /* positioned absolute to parent at end position, to stay within content box */
    position: absolute;
    top: 0;
    left: var(--input-padding);
    line-height: var(--input-margin);

    /* set up label animation when focused */
    transition: transform 150ms ease-out, font-size 150ms ease-out;
    font-size: var(--value-font-size);
    /* give element start position w/ transform */
    transform: translateY(calc(var(--input-padding) + var(--input-margin)));
  }

  .success {
    max-width: 1rem;
    position: absolute;
    top: 45px;
    right: 0.6rem;
    top: calc(var(--input-margin) + var(--input-padding));
  }
  .icon {
    max-width: 1rem;
    position: absolute;
    top: 45px;
    left: 0.6rem;
    top: calc(var(--input-margin) + var(--input-padding));
  }

  .status-line {
    font-size: 0.8rem;
    display: flex;
    justify-content: space-between;

    /* this splits the input margin above and below the div */
    transform: translateY(calc(var(--input-margin) / -2));

    .error-style {
      color: ${colors.recordRed};
    }

    .counter {
      color: ${colors.silverSandAlt};
    }
  }

  &.focused label {
    transform: none;
    font-size: 0.75rem;
  }

  &.outline {
    input {
      border: 1.5px solid ${colors.disableGrey};
      border-radius: 2px;
      position: relative;
      top: 20px;

      &:focus {
        border-color: ${colors.electricBlue};
      }

      &.error {
        border-color: ${colors.recordRed};
      }
    }

    label {
      left: 0;
      transform: none;
      font-size: 0.75rem;
      color: ${colors.battleship};
    }

    .success {
      top: 3.313rem;
    }
    .icon {
      top: 3.313rem;
    }

    .status-line {
      transform: translateY(5px);
    }
  }

  &.rebrand {
    ${regular()}
    input {
      ${regular()}
      border-color: ${colors.disableGrey};
      font-size: 1rem;
      height: 2rem;
      padding-right: ${(props) => (props.showSuccess ? '1.375rem' : 'var(--input-padding)')};
      padding-left: var(--icon-padding);

      &:focus {
        border-color: ${colors.electricBlue};
      }

      &.error {
        border-color: ${colors.recordRed};
      }
    }

    .success {
      top: calc(var(--input-margin) + 0.406rem);
      right: 0.375rem;
    }
  }

  &.rebrand.outline {
    .success {
      top: 2.938rem;
    }
    .icon {
      top: 2.938rem;
    }
  }
`;

export default styledInputField;
